import { useEffect, useMemo, useState } from 'react';
import { api, type OpsChange } from './api';
import { ts, useDebounced } from './lib';
import type { Body, Category, Json } from './records-api';

// Building blocks for the master-record editors (panels/Camps.tsx, panels/VendorRecord.tsx): a grid of
// labeled fields whose save sends only what changed, list editors, and the change log.

export type FieldKind = 'text' | 'email' | 'url' | 'number' | 'select' | 'bool' | 'textarea';
export interface FieldDef {
  /** request-body key */
  key: string;
  /** record column it reads from */
  col: string;
  label: string;
  kind: FieldKind;
  options?: Array<string | { value: string; label: string }>;
  placeholder?: string;
}
export type Form = Record<string, string>;

/** A record's values as form strings: null → '', booleans → 'true' | 'false'. */
export function toForm(defs: FieldDef[], rec: Record<string, unknown> | null | undefined): Form {
  return Object.fromEntries(defs.map((d) => {
    const v = rec?.[d.col];
    return [d.key, v == null ? '' : String(v)];
  }));
}

/** The fields whose value differs from `initial`, as the API takes them ('' → null). */
export function diff(defs: FieldDef[], initial: Form, current: Form): Body {
  const out: Body = {};
  for (const d of defs) {
    if ((initial[d.key] ?? '') === (current[d.key] ?? '')) continue;
    const v = (current[d.key] ?? '').trim();
    out[d.key] = d.kind === 'bool' ? (v === '' ? null : v === 'true') : v === '' ? null : (current[d.key] ?? '');
  }
  return out;
}

const optionsOf = (d: FieldDef) => (d.options ?? []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));

/** Labeled inputs in a responsive grid. Disabled for staff who may only read. */
export function FieldGrid({ defs, form, onChange, disabled }: {
  defs: FieldDef[]; form: Form; onChange: (f: Form) => void; disabled?: boolean;
}) {
  const set = (k: string, v: string) => onChange({ ...form, [k]: v });
  return (
    <div className="tf-grid mr-grid">
      {defs.map((d) => (
        <label key={d.key} className={d.kind === 'textarea' ? 'mr-span' : undefined}>
          {d.label}
          {d.kind === 'select' || d.kind === 'bool' ? (
            <select value={form[d.key] ?? ''} disabled={disabled} onChange={(e) => set(d.key, e.target.value)}>
              <option value="">—</option>
              {(d.kind === 'bool' ? [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] : optionsOf(d)).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : d.kind === 'textarea' ? (
            <textarea className="search wide mr-textarea" rows={3} value={form[d.key] ?? ''} disabled={disabled} placeholder={d.placeholder}
              onChange={(e) => set(d.key, e.target.value)} />
          ) : (
            <input type={d.kind === 'number' ? 'text' : d.kind === 'email' ? 'text' : d.kind} inputMode={d.kind === 'number' ? 'decimal' : undefined}
              value={form[d.key] ?? ''} disabled={disabled} placeholder={d.placeholder ?? '—'} onChange={(e) => set(d.key, e.target.value)} />
          )}
        </label>
      ))}
    </div>
  );
}

/** A form bound to one record: its initial values, the edits, and what a save would send. */
export function useRecordForm(defs: FieldDef[], rec: Record<string, unknown> | null | undefined) {
  const initial = useMemo(() => toForm(defs, rec), [defs, rec]);
  const [form, setForm] = useState<Form>(initial);
  useEffect(() => setForm(initial), [initial]);
  const changes = diff(defs, initial, form);
  return { form, setForm, changes, dirty: Object.keys(changes).length > 0, reset: () => setForm(initial) };
}

/** What the last save of one card answered. */
export interface Outcome { tag: string; text: string; bad: boolean }

/** A save's answer, shown where the person clicked. */
export function OutcomeLine({ o }: { o: Outcome | null | undefined }) {
  if (!o) return null;
  return <span className={o.bad ? 'mr-bad' : 'mr-good'} role="status">{o.bad ? `⚠ ${o.text}` : o.text}</span>;
}

/** Save / discard controls under a form, with the outcome of the last save. */
export function SaveBar({ dirty, busy, onSave, onReset, disabled, hint, outcome }: {
  dirty: boolean; busy: boolean; onSave: () => void; onReset?: () => void; disabled?: boolean; hint?: string; outcome?: Outcome | null;
}) {
  return (
    <div className="edi-actions">
      {outcome ? <OutcomeLine o={outcome} /> : <span className="muted">{hint}</span>}
      <div className="actions">
        {onReset && dirty && <button className="btn-sm ghost" disabled={busy} onClick={onReset}>Discard</button>}
        <button className="btn-sm" disabled={!dirty || busy || disabled} onClick={onSave}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
}

/** A list of short strings (GL codes) edited as removable chips plus an add box. */
export function ChipListEditor({ items, onChange, disabled, placeholder }: {
  items: string[]; onChange: (next: string[]) => void; disabled?: boolean; placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (t && !items.includes(t)) onChange([...items, t]);
    setDraft('');
  };
  return (
    <div className="mr-chips">
      {items.map((x) => (
        <span key={x} className="pill mr-chip">
          {x}
          {!disabled && <button className="mr-x" aria-label={`Remove ${x}`} onClick={() => onChange(items.filter((y) => y !== x))}>×</button>}
        </span>
      ))}
      {items.length === 0 && <span className="muted">None.</span>}
      {!disabled && (
        <span className="mr-add">
          <input className="search" value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <button className="btn-sm ghost" disabled={!draft.trim()} onClick={add}>Add</button>
        </span>
      )}
    </div>
  );
}

/** Category ids edited by name: the chosen ones as chips, a search over the tree to add more. */
export function CategoryPicker({ categories, ids, onChange, disabled }: {
  categories: Category[]; ids: number[]; onChange: (next: number[]) => void; disabled?: boolean;
}) {
  const [q, setQ] = useState('');
  const byId = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const matches = q.trim().length < 2 ? [] : categories
    .filter((c) => !ids.includes(c.id) && c.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 12);
  return (
    <div className="stack mr-tight">
      <div className="mr-chips">
        {ids.map((id) => (
          <span key={id} className="pill mr-chip" title={`category ${id}`}>
            {byId.get(id)?.name ?? `#${id}`}
            {!disabled && <button className="mr-x" aria-label="Remove" onClick={() => onChange(ids.filter((x) => x !== id))}>×</button>}
          </span>
        ))}
        {ids.length === 0 && <span className="muted">Nothing blocked.</span>}
      </div>
      {!disabled && (
        <div className="field">
          <input className="search" value={q} placeholder="Search categories to block…" onChange={(e) => setQ(e.target.value)} />
          {matches.length > 0 && (
            <div className="typeahead">
              {matches.map((c) => (
                <button key={c.id} className="ta-opt" onClick={() => { onChange([...ids, c.id]); setQ(''); }}>{c.name}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Order-limit periods and package-email choices as staff read them (Intellam's OrderAmountLimit and GetPackageEmail). */
export const LIMIT_LABEL: Record<string, string> = { day: 'per day', month: 'per month', year: 'per year', order: 'per order' };
export const PACKAGE_LABEL: Record<string, string> = {
  none: 'No SUM', sum: 'SUM', sum_remittance: 'SUM + remittance', sum_remittance_split: 'SUM + remittance (split)',
};

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Weekday toggles, 0=Sunday..6=Saturday. No day picked means any day. */
export function WeekdayPicker({ days, onChange, disabled }: { days: number[]; onChange: (d: number[]) => void; disabled?: boolean }) {
  return (
    <div className="toolbar">
      {WEEKDAYS.map((w, i) => (
        <button key={w} type="button" className={`chip ${days.includes(i) ? 'on' : ''}`} disabled={disabled}
          onClick={() => onChange(days.includes(i) ? days.filter((d) => d !== i) : [...days, i].sort((a, b) => a - b))}>{w}</button>
      ))}
      {days.length === 0 && <span className="muted small">any day</span>}
    </div>
  );
}

/** org_vendor's comma list ('1,3,5') → [1, 3, 5]. Blank, and anything that is not a weekday, drops out. */
export const parseDays = (v: string | null | undefined) =>
  String(v ?? '').split(',').map((x) => x.trim()).filter((x) => /^[0-6]$/.test(x)).map(Number);

/** '1,3,5' (org_vendor) or [1,3,5] → 'Mon, Wed, Fri'; none → 'any day'. */
export const dayNames = (v: string | number[] | null | undefined) => {
  const days = Array.isArray(v) ? v : parseDays(v);
  return days.length ? days.map((d) => WEEKDAYS[d]).join(', ') : 'any day';
};

/** Pick a vendor by name (the Vendors list search). */
export function VendorPicker({ onPick, placeholder, exclude = [] }: { onPick: (id: number, name: string) => void; placeholder?: string; exclude?: number[] }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [rows, setRows] = useState<Array<{ id: number; name: string }>>([]);
  useEffect(() => {
    if (dq.trim().length < 2) return setRows([]);
    let live = true;
    api.commerceVendors(dq.trim()).then((r) => live && setRows(r.filter((v) => !exclude.includes(v.id)).slice(0, 10))).catch(() => live && setRows([]));
    return () => { live = false; };
  }, [dq]);
  return (
    <div className="field">
      <input className="search" value={q} placeholder={placeholder ?? 'Search vendors…'} onChange={(e) => setQ(e.target.value)} />
      {rows.length > 0 && q.trim().length >= 2 && (
        <div className="typeahead">
          {rows.map((v) => (
            <button key={v.id} className="ta-opt" onClick={() => { onPick(v.id, v.name); setQ(''); setRows([]); }}>{v.name} <span className="mono">#{v.id}</span></button>
          ))}
        </div>
      )}
    </div>
  );
}

const ENTITY_LABEL: Record<string, string> = {
  org: 'camp', camp_profile: 'profile', camp_user: 'user', org_vendor: 'vendor pair', vendor: 'vendor',
  vendor_profile: 'record', vendor_feed_map: 'feed map', app_user_permission: 'user', permission_template: 'template',
};
const short = (v: string | null) => (v == null ? '—' : v.length > 60 ? `${v.slice(0, 57)}…` : v);

/** Who changed what, newest first (commerce.ops_change_log). */
export function ChangesCard({ changes }: { changes: OpsChange[] }) {
  if (changes.length === 0) return null;
  return (
    <section className="card">
      <h2>Recent changes</h2>
      <div className="mr-scroll">
        <table>
          <thead><tr><th>When</th><th>Who</th><th>What</th><th>From</th><th>To</th></tr></thead>
          <tbody>
            {changes.map((c, i) => (
              <tr key={i}>
                <td className="mono">{ts(c.changed_at)}</td>
                <td>{c.changed_by}</td>
                <td>{ENTITY_LABEL[c.entity] ?? c.entity}{c.entity === 'camp_user' || c.entity === 'org_vendor' || c.entity === 'app_user_permission' ? ` ${c.entity_id}` : ''} · {c.field.replace(/_/g, ' ')}</td>
                <td className="mono" title={c.old_value ?? ''}>{short(c.old_value)}</td>
                <td className="mono" title={c.new_value ?? ''}>{short(c.new_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Adobe is still the live system until the cutover; every master-record screen says so once. */
export function SystemOfRecordNote() {
  return (
    <p className="note">
      Adobe is still the live system until the cutover — make the same change there too. The data refresh from Adobe keeps
      what you save here, and lists any field Adobe has changed since.
    </p>
  );
}

export const errText = (e: unknown) => (e as Error)?.message ?? String(e);
export type { Json };
